// Pantalla única que cubre DOS casos con la misma acción de fondo
// (`supabase.auth.updateUser({ password })`, que no exige la contraseña
// anterior porque ya valida identidad vía la sesión activa):
//
// 1. Cambio voluntario: admin_empresa/editor_empresa con sesión normal,
//    llega acá desde el enlace "Cambiar contraseña" del topbar (shell.js).
// 2. Recuperación: alguien que perdió su contraseña pidió el enlace desde
//    app.vimosdigital.com (App Comercial), y estado-sesion.js lo trajo
//    acá automáticamente al detectar el evento PASSWORD_RECOVERY.
//
// No se registra como ruta protegida por `exigirSesionEmpresa` (guardas.js)
// porque esa guarda exige rol+empresa_id ya resueltos en el JWT, y una
// sesión de recuperación recién creada podría llegar un instante antes de
// que el Auth Hook los adjunte (ver ADR-0005) — acá solo se exige que
// exista ALGUNA sesión; sin ella no hay nada que cambiar.

import { mostrarToast } from '@plataforma/ui';

import { navegarA } from '../enrutador.js';
import { actualizarContrasena, obtenerEstadoSesion } from '../estado-sesion.js';

import { montarShell } from './shell.js';

const LONGITUD_MINIMA = 8;

export function vistaCambiarContrasena(contenedorRaiz) {
  const { sesion, claims } = obtenerEstadoSesion();

  if (!sesion) {
    navegarA('/login');
    contenedorRaiz.innerHTML = `
      <div class="pf-login">
        <div class="pf-tarjeta pf-login__tarjeta" style="text-align:center;">
          <h1 class="pf-login__titulo">Enlace vencido</h1>
          <p class="pf-texto-suave">
            Este enlace para restablecer tu contraseña ya no es válido. Pide uno nuevo desde la pantalla de inicio de sesión.
          </p>
        </div>
      </div>
    `;
    return;
  }

  // Con claims completos (caso 1, uso normal) se muestra dentro del shell
  // habitual; sin ellos (caso 2, recién llegado desde el correo, un
  // instante antes de que el Auth Hook los adjunte) se muestra como
  // pantalla suelta, igual que /login — el shell necesita rol+empresa_id
  // para pintar el menú de navegación.
  const dentroDelShell =
    ['admin_empresa', 'editor_empresa'].includes(claims.rol) && Boolean(claims.empresa_id);

  const contenido = dentroDelShell
    ? montarShell(contenedorRaiz, '')
    : (() => {
        contenedorRaiz.innerHTML = `<div class="pf-login"><div class="pf-tarjeta pf-login__tarjeta" id="pf-cc-tarjeta"></div></div>`;
        return contenedorRaiz.querySelector('#pf-cc-tarjeta');
      })();

  contenido.innerHTML = `
    <h1 class="${dentroDelShell ? '' : 'pf-login__titulo'}">Cambiar contraseña</h1>
    <p class="pf-texto-suave" style="margin: 4px 0 16px;">
      Elige una nueva contraseña de al menos ${LONGITUD_MINIMA} caracteres.
    </p>
    <form id="pf-form-cambiar-contrasena" novalidate>
      <div class="pf-campo">
        <label for="pf-cc-nueva">Nueva contraseña</label>
        <input type="password" id="pf-cc-nueva" autocomplete="new-password" required minlength="${LONGITUD_MINIMA}" />
      </div>
      <div class="pf-campo">
        <label for="pf-cc-confirmar">Confirmar contraseña</label>
        <input type="password" id="pf-cc-confirmar" autocomplete="new-password" required minlength="${LONGITUD_MINIMA}" />
      </div>
      <p class="pf-campo__error" id="pf-cc-error" hidden></p>
      <div class="pf-fila" style="justify-content: flex-end;">
        <button type="submit" class="pf-boton pf-boton--primario" id="pf-cc-submit">Guardar</button>
      </div>
    </form>
  `;

  const form = contenido.querySelector('#pf-form-cambiar-contrasena');
  const errorEl = contenido.querySelector('#pf-cc-error');
  const submitEl = contenido.querySelector('#pf-cc-submit');

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;

    const nueva = contenido.querySelector('#pf-cc-nueva').value;
    const confirmar = contenido.querySelector('#pf-cc-confirmar').value;

    if (nueva.length < LONGITUD_MINIMA) {
      errorEl.textContent = `La contraseña debe tener al menos ${LONGITUD_MINIMA} caracteres.`;
      errorEl.hidden = false;
      return;
    }
    if (nueva !== confirmar) {
      errorEl.textContent = 'Las contraseñas no coinciden.';
      errorEl.hidden = false;
      return;
    }

    submitEl.disabled = true;
    submitEl.textContent = 'Guardando…';

    const { error } = await actualizarContrasena(nueva);

    submitEl.disabled = false;
    submitEl.textContent = 'Guardar';

    if (error) {
      errorEl.textContent = 'No se pudo actualizar la contraseña. Intenta de nuevo.';
      errorEl.hidden = false;
      return;
    }

    mostrarToast('Contraseña actualizada.', { tono: 'exito' });
    navegarA('/');
  });
}
